import { FC } from "react";

import { Tag as BaseTag, TagType } from "../../Tag";
import { BaseColumnProps } from "../BaseColumnProps";
import { ColumnElementConstructor } from "../ColumnElementConstructor";
import { Custom } from "./Custom";

/** The `Table.Tag` column tag type provider function. */
export interface TableColumnTagTypeProvider {
  /**
   * Gets the tag type for the specified value.
   * @param value The value to get the tag type for.
   */
  (value: string | number): TagType | undefined;
}

/**
 * The `Tag` component properties.
 */
export interface TagProps extends Omit<BaseColumnProps, "align"> {
  /**
   * A function which takes the value and provides a tag type. If not specified defaults to a provider which
   * generates `"info"` type tags.
   */
  readonly type?: TableColumnTagTypeProvider;
}

/**
 * The default tag type provider.
 */
function DefaultType(): TagType {
  return "info";
}

/**
 * Column which displays a value as one or more tags.
 */
export const Tag: FC<TagProps> = (props) => {
  const { type = DefaultType } = props;

  return (
    <Custom {...props}>
      {(value) => {
        if (value === null) return "--";
        if (value === undefined) return "--";

        if (Array.isArray(value)) {
          return (
            <>
              {value.map((entry) => {
                if (typeof entry === "string" || typeof entry === "number") {
                  const tagType = type(entry) ?? "info";

                  return <BaseTag type={tagType}>{entry}</BaseTag>;
                }
              })}
            </>
          );
        } else {
          if (typeof value === "string" || typeof value === "number") {
            const tagType = type(value) ?? "info";

            return <BaseTag type={tagType}>{value}</BaseTag>;
          }
        }

        return "--";
      }}
    </Custom>
  );
};

(Tag as ColumnElementConstructor<TagProps>).className = "tag";
/* Tag column headers are left-aligned to match standard column alignment — tags are inline elements that read naturally left-to-right. */
(Tag as ColumnElementConstructor<TagProps>).headerAlignment = "left";
